import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useHistory, useParams, useLocation, Link, Prompt } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import BackLink from '../components/BackLink';
import LogoSpinner from '../components/ui/LogoSpinner';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import LocationPicker from '../components/LocationPicker';
import RichTextArea from '../components/RichTextArea';
import TransportChips from '../components/TransportChips';
import UserPicker from '../components/UserPicker';
import MessageThread from '../components/MessageThread/MessageThread';
import EventAirspaceWidget from './EventAirspaceWidget';
import EventAirspaceModal from './EventAirspaceModal';
import AirspaceAdvisoryPanel from './AirspaceAdvisoryPanel';
import { useSiteMapShare, parseShareUrl } from './useSiteMapShare';
import EditLockGuard, { useEditLockSession } from '../components/EditLockGuard';
import {
  EVENT_LOCKS,
  isFormFieldIncluded, resolveFormValue,
  defaultLockFlags, hydrateLockFlags, extractLockFlags,
} from './event-locks';
import dayjs from 'dayjs';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Tag from '../components/ui/Tag';
import Chip from '../components/ui/Chip';
import ChipToggle from '../components/ui/ChipToggle';
import Switch from '../components/ui/Switch';
import Select from '../components/ui/Select';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { CATEGORY, alpha } from '../components/ui/categoryColors';
import Tabs from '../components/ui/Tabs';
import { Field, TextArea } from '../components/ui/Field';
import { IconClose } from '../components/ui/icons';
import { formatEventDate, shiftDay } from '../utils/date-utils';

const FORM_TABS = ['main', 'location', 'creative', 'logistics', 'contacts'];

// Server shape → editable rows. Defensive about both fields because the column
// is JSONB: a hand-written row or an older client could leave either missing,
// and an undefined value in a controlled <input> flips it to uncontrolled.
const toPreviewRows = (v) =>
  (Array.isArray(v) ? v : []).map(l => ({ label: l?.label || '', url: l?.url || '' }));

// resolveFormValue returns raw form state, and isFilled() counts any array with
// length as filled. A just-added preview row with no URL yet is dropped on save,
// so scoring it would read 1/5 in the Creative ring before saving and 0/5 after,
// and would tell LockButton the scope has no blanks when it does. The ring and
// the pre-lock blank count both read through here.
const scoredFormValue = (fieldDesc, form, crewCount, contactsArr) => {
  const v = resolveFormValue(fieldDesc, form, crewCount, contactsArr);
  return fieldDesc === 'preview_links' ? (v || []).filter(l => (l.url || '').trim()) : v;
};

const EventForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const isEdit = !!id;
  const queryDate = new URLSearchParams(location.search).get('date') || '';

  // Bridge ref populated by <LockSessionBridge/> (rendered inside EditLockGuard).
  // Lets the save handler — defined out here at the top level of EventForm — read
  // the lock session id and signal the guard when the server reports a lost lock.
  const lockCtxRef = useRef(null);

  // Set immediately before the post-save navigation so <Prompt> lets it through.
  // See the comment on <Prompt> below.
  const leavingAfterSaveRef = useRef(false);

  // Tab travels in the URL so the editor opens where the read view left off,
  // and Cancel / Save land back on that same tab. Unknown values fall back to
  // main rather than rendering a blank body.
  // Written back on every change, like the read view does, so that a refresh or
  // EditLockGuard's lock-lost window.location.reload() returns you to the tab
  // you were working in rather than the one you happened to arrive on.
  const initialTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTabState] = useState(FORM_TABS.includes(initialTab) ? initialTab : 'main');
  const setActiveTab = (key) => {
    setActiveTabState(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };
  const [airspaceModalOpen, setAirspaceModalOpen] = useState(false);
  // (A save used to bump a refreshKey here so the QRD chip refetched in place.
  //  Save now navigates to the event page, which mounts its own widget with
  //  fresh data, so the counter had no reader left.)
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const [showPendingNearby, setShowPendingNearby] = useState(false);
  const [showAudience, setShowAudience] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState([]);
  const [timezone, setTimezone] = useState('');

  const [sunset, setSunset] = useState(null);
  const [crew, setCrew] = useState([]);           // [{user_id, role, notes, firstname, lastname}]
  const [allUsers, setAllUsers] = useState([]);    // from GET /users/active
  const [showLegacyCrew, setShowLegacyCrew] = useState(false);
  const [crewSnapshot, setCrewSnapshot] = useState(null);
  // External crew: free-form people who aren't Firefleet users (contractors, client helpers).
  // Shape: [{ id?, name, email, phone, notes }]. id is server-assigned after save.
  const [externalCrew, setExternalCrew] = useState([]);
  const [externalCrewSnapshot, setExternalCrewSnapshot] = useState(null);

  const [form, setForm] = useState({
    name: '',
    event_date: queryDate,
    end_date: '',
    rain_date: '',
    show_time: '',
    second_show_time: '',
    location: '',
    status: 'pending',
    total_drone_count: 0,
    pyro_drone_count: 0,
    buffer_percentage: 10,
    is_double_show: false,
    tour_id: null,
    show_height_ft: '',
    // Ordered [{label, url}] — see migration 041. Rows are added/removed in the
    // Creative tab; a blank row is dropped on save rather than persisted.
    preview_links: [],
    site_map_url: '',
    contacts: [],
    launch_latitude: null,
    launch_longitude: null,
    audience_latitude: null,
    audience_longitude: null,
    surface_type: '',
    timezone: '',
    ship_out_date: '',
    ship_back_date: '',
    transport_mode: 'shipping',
    // Read-only here, for the Transport chips. Owned by the Transport Scheduler and
    // stripped from the PATCH below with the other scheduler-owned fields.
    transport_arranged: false,
    tracking_number_out: '',
    tracking_number_return: '',
    notes: '',
    details_json: {
      music: '',
      time_machine: '',
      music_link_code: '',
      load_in: '',
      sound_check: '',
      four_corners: '',
      sunset_time: '',
      perimeter_secured: '',
      perimeter_lifted: '',
      load_out: '',
      crew: '',
      gear_transportation: '',
      flight: '',
      rental_car: '',
      box_truck: '',
      hotel: '',
      packing: '',
      creative: '',
      onsite_contact_name: '',
      onsite_contact_phone: '',
    },
    // Completion-ring lock-in flags (all default false; see event-locks.js)
    ...defaultLockFlags(),
  });

  // Snapshot for dirty tracking — set after initial load
  const [formSnapshot, setFormSnapshot] = useState(null);

  // Live validation/lookup for the producer site map URL the user is typing.
  // Drives the status pill under the input on the Location tab.
  const siteMapShare = useSiteMapShare(form.site_map_url);

  // Field-to-tab mapping for per-tab dirty indicators.
  // Lock keys come from EVENT_LOCKS so adding a lock auto-propagates here.
  const TAB_FIELDS = useMemo(() => {
    const locksByTab = EVENT_LOCKS.reduce((acc, l) => {
      (acc[l.tab] = acc[l.tab] || []).push(l.key);
      return acc;
    }, {});
    return {
      main: ['name', 'event_date', 'end_date', 'rain_date', 'show_time', 'second_show_time', 'status', 'total_drone_count', 'pyro_drone_count', 'buffer_percentage', 'is_double_show', 'tour_id', 'notes', ...(locksByTab.main || [])],
      location: ['location', 'launch_latitude', 'launch_longitude', 'audience_latitude', 'audience_longitude', 'show_height_ft', 'surface_type', 'site_map_url', ...(locksByTab.location || [])],
      creative: ['preview_links', ...(locksByTab.creative || [])],
      logistics: ['ship_out_date', 'ship_back_date', 'transport_mode', 'tracking_number_out', 'tracking_number_return', ...(locksByTab.logistics || [])],
      contacts: ['contacts', ...(locksByTab.contacts || [])],
    };
  }, []);

  // details_json sub-fields per tab
  const TAB_DETAIL_FIELDS = useMemo(() => ({
    main: ['load_in', 'sound_check', 'four_corners', 'perimeter_secured', 'perimeter_lifted', 'load_out'],
    location: [],
    creative: ['music', 'time_machine', 'music_link_code', 'creative'],
    logistics: ['gear_transportation', 'flight', 'rental_car', 'box_truck', 'hotel', 'packing'],
    contacts: ['onsite_contact_name', 'onsite_contact_phone'],
  }), []);

  const isTabDirty = useCallback((tab) => {
    if (!formSnapshot) return false;
    const snap = formSnapshot;
    // Check top-level fields
    for (const key of TAB_FIELDS[tab] || []) {
      // Arrays of objects need a structural compare. The String() fallback below
      // would stringify them to "[object Object]" and never see an edit.
      if (key === 'contacts' || key === 'preview_links') {
        if (JSON.stringify(form[key]) !== JSON.stringify(snap[key])) return true;
      } else if (String(form[key] ?? '') !== String(snap[key] ?? '')) return true;
    }
    // Check details_json sub-fields
    for (const key of TAB_DETAIL_FIELDS[tab] || []) {
      if (String(form.details_json[key] ?? '') !== String(snap.details_json?.[key] ?? '')) return true;
    }
    // Crew assignments (logistics tab) — internal + external both live under the same lock
    if (tab === 'logistics' && crewSnapshot) {
      if (JSON.stringify(crew) !== JSON.stringify(crewSnapshot)) return true;
    }
    if (tab === 'logistics' && externalCrewSnapshot) {
      if (JSON.stringify(externalCrew) !== JSON.stringify(externalCrewSnapshot)) return true;
    }
    return false;
  }, [form, crew, externalCrew, formSnapshot, crewSnapshot, externalCrewSnapshot, TAB_FIELDS, TAB_DETAIL_FIELDS]);

  const anyDirty = useMemo(() => ['main', 'location', 'creative', 'logistics', 'contacts'].some(t => isTabDirty(t)), [isTabDirty]);

  // Live completion per tab — iterates EVENT_LOCKS config so adding a lock
  // auto-propagates here. Mirrors backend compute-completion.js.
  const isFilled = (v) => v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim().length > 0 : true);
  const tabCompletion = useMemo(() => {
    const tabs = { main: [], location: [], creative: [], logistics: [], contacts: [] };
    for (const lock of EVENT_LOCKS) {
      const isLocked = !!form[lock.key];
      for (const fieldDesc of lock.fields) {
        if (!isFormFieldIncluded(fieldDesc, form)) continue;
        const value = scoredFormValue(fieldDesc, form, crew.length, form.contacts);
        tabs[lock.tab].push([value, isLocked]);
      }
    }
    const result = {};
    for (const [tab, fields] of Object.entries(tabs)) {
      let filled = 0, locked = 0;
      for (const [v, isLocked] of fields) {
        if (isFilled(v)) filled += 1;
        if (isLocked) locked += 1;
      }
      result[tab] = { filled, locked, total: fields.length };
    }
    return result;
  }, [form, crew]);

  // Raw scope values per lockable scope — used by LockButton to count blanks
  // before locking. Built from EVENT_LOCKS so adding a lock auto-propagates.
  const lockScopes = useMemo(() => {
    const scopes = {};
    for (const lock of EVENT_LOCKS) {
      scopes[lock.key] = lock.fields
        .filter(fd => isFormFieldIncluded(fd, form))
        .map(fd => scoredFormValue(fd, form, crew.length, form.contacts));
    }
    return scopes;
  }, [form, crew]);

  // Browser beforeunload warning
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  // Read-only: vehicles assigned to this event (event_vehicle), surfaced in the
  // Transport & Shipping section. Assignment is managed in the Transport Scheduler.
  const [assignedVehicles, setAssignedVehicles] = useState([]);

  useEffect(() => {
    if (isEdit) {
      const fetchEvent = async () => {
        try {
          const event = await api.get(`/events/${id}`);
          setAssignedVehicles(event.vehicles || []);
          setForm({
            name: event.name || '',
            event_date: event.event_date?.split('T')[0] || '',
            end_date: event.end_date?.split('T')[0] || '',
            rain_date: event.rain_date?.split('T')[0] || '',
            show_time: event.show_time || '',
            second_show_time: event.second_show_time || '',
            location: event.location || '',
            status: event.status || 'pending',
            total_drone_count: (event.ls_drone_count || 0) + (event.pyro_drone_count || 0),
            pyro_drone_count: event.pyro_drone_count || 0,
            buffer_percentage: event.buffer_percentage ?? 10,
            is_double_show: event.is_double_show || false,
            tour_id: event.tour_id || null,
            show_height_ft: event.show_height_ft || '',
            preview_links: toPreviewRows(event.preview_links),
            site_map_url: event.site_map_url || '',
            contacts: event.contacts || [],
            launch_latitude: event.launch_latitude || null,
            launch_longitude: event.launch_longitude || null,
            audience_latitude: event.audience_latitude || null,
            audience_longitude: event.audience_longitude || null,
            surface_type: event.surface_type || '',
            timezone: event.timezone || '',
            ship_out_date: event.ship_out_date?.split('T')[0] || '',
            ship_back_date: event.ship_back_date?.split('T')[0] || '',
            transport_mode: event.transport_mode || 'shipping',
            transport_arranged: !!event.transport_arranged,
            tracking_number_out: event.tracking_number_out || '',
            tracking_number_return: event.tracking_number_return || '',
            notes: event.notes || '',
            details_json: {
              music: '', time_machine: '', music_link_code: '',
              load_in: '', sound_check: '', four_corners: '', sunset_time: '',
              perimeter_secured: '', perimeter_lifted: '', load_out: '',
              crew: '', gear_transportation: '', flight: '', rental_car: '',
              box_truck: '', hotel: '', packing: '', creative: '',
              onsite_contact_name: '', onsite_contact_phone: '',
              ...(event.details_json || {}),
            },
            ...hydrateLockFlags(event),
          });
          // Snapshot for dirty tracking — must match the setForm shape above
          setFormSnapshot({
            name: event.name || '',
            event_date: event.event_date?.split('T')[0] || '',
            end_date: event.end_date?.split('T')[0] || '',
            rain_date: event.rain_date?.split('T')[0] || '',
            show_time: event.show_time || '',
            second_show_time: event.second_show_time || '',
            location: event.location || '',
            status: event.status || 'pending',
            total_drone_count: (event.ls_drone_count || 0) + (event.pyro_drone_count || 0),
            pyro_drone_count: event.pyro_drone_count || 0,
            buffer_percentage: event.buffer_percentage ?? 10,
            is_double_show: event.is_double_show || false,
            tour_id: event.tour_id || null,
            show_height_ft: event.show_height_ft || '',
            preview_links: toPreviewRows(event.preview_links),
            site_map_url: event.site_map_url || '',
            contacts: JSON.parse(JSON.stringify(event.contacts || [])),
            launch_latitude: event.launch_latitude || null,
            launch_longitude: event.launch_longitude || null,
            audience_latitude: event.audience_latitude || null,
            audience_longitude: event.audience_longitude || null,
            surface_type: event.surface_type || '',
            timezone: event.timezone || '',
            ship_out_date: event.ship_out_date?.split('T')[0] || '',
            ship_back_date: event.ship_back_date?.split('T')[0] || '',
            transport_mode: event.transport_mode || 'shipping',
            transport_arranged: !!event.transport_arranged,
            tracking_number_out: event.tracking_number_out || '',
            tracking_number_return: event.tracking_number_return || '',
            notes: event.notes || '',
            details_json: {
              music: '', time_machine: '', music_link_code: '',
              load_in: '', sound_check: '', four_corners: '', sunset_time: '',
              perimeter_secured: '', perimeter_lifted: '', load_out: '',
              crew: '', gear_transportation: '', flight: '', rental_car: '',
              box_truck: '', hotel: '', packing: '', creative: '',
              onsite_contact_name: '', onsite_contact_phone: '',
              ...(event.details_json || {}),
            },
            ...hydrateLockFlags(event),
          });
          if (event.audience_latitude) setShowAudience(true);
          if (event.timezone) setTimezone(event.timezone);

          // Fetch crew assignments
          try {
            const crewData = await api.get(`/events/${id}/crew`, { silent: true });
            const mapped = (crewData || []).map(c => ({ user_id: c.user_id, role: c.role || '', notes: c.notes || '', firstname: c.firstname, lastname: c.lastname }));
            setCrew(mapped);
            setCrewSnapshot(JSON.parse(JSON.stringify(mapped)));
            // Show legacy banner if old text exists
            if (event.details_json?.crew) setShowLegacyCrew(true);
          } catch { setCrewSnapshot([]); }

          // Fetch external crew assignments (contractors / non-user crew)
          try {
            const extData = await api.get(`/events/${id}/external-crew`, { silent: true });
            const extMapped = (extData || []).map(c => ({
              id: c.id,
              name: c.name || '',
              email: c.email || '',
              phone: c.phone || '',
              notes: c.notes || '',
            }));
            setExternalCrew(extMapped);
            setExternalCrewSnapshot(JSON.parse(JSON.stringify(extMapped)));
          } catch { setExternalCrewSnapshot([]); }
        } catch (error) {
          toast.error('Event not found');
          history.push('/events');
        } finally {
          setLoading(false);
        }
      };
      fetchEvent();
    } else {
      // Create mode — snapshot is the initial blank form
      setFormSnapshot(JSON.parse(JSON.stringify(form)));
      setCrewSnapshot([]);
      setExternalCrewSnapshot([]);
    }

    // Fetch active users for crew picker (both create and edit)
    api.get('/users/active', { silent: true }).then(u => setAllUsers(u || [])).catch(() => {});
  }, [id]);

  // Fetch availability for the selected date
  useEffect(() => {
    if (!form.event_date) { setAvailability(null); return; }
    const fetchAvail = async () => {
      try {
        const params = new URLSearchParams({ date: form.event_date });
        if (isEdit) params.set('exclude_event_id', id);
        const data = await api.get(`/events/availability?${params}`);
        setAvailability(data);
      } catch (error) {
        // silent fail
      }
    };
    fetchAvail();
  }, [id, form.event_date]);

  // Fetch nearby events (±10 days)
  useEffect(() => {
    if (!form.event_date) { setNearbyEvents([]); return; }
    const from = dayjs(form.event_date).subtract(10, 'day').format('YYYY-MM-DD');
    const to = dayjs(form.event_date).add(10, 'day').format('YYYY-MM-DD');
    const fetchNearby = async () => {
      try {
        const data = await api.get(`/events?from_date=${from}&to_date=${to}`);
        setNearbyEvents((data || []).filter(e => e.id !== id));
      } catch { setNearbyEvents([]); }
    };
    fetchNearby();
  }, [form.event_date, id]);

  // Auto-detect timezone from launch coordinates
  useEffect(() => {
    if (!form.launch_latitude || !form.launch_longitude) return;
    const fetchTz = async () => {
      try {
        const res = await fetch(`https://timeapi.io/api/timezone/coordinate?latitude=${form.launch_latitude}&longitude=${form.launch_longitude}`);
        const data = await res.json();
        if (data.timeZone) {
          setTimezone(data.timeZone);
          setForm(f => ({ ...f, timezone: data.timeZone }));
        }
      } catch {
        // silent fail — timezone is nice-to-have
      }
    };
    fetchTz();
  }, [form.launch_latitude, form.launch_longitude]);

  // Fetch sunset time when date + coordinates are set
  useEffect(() => {
    if (!form.event_date || !form.launch_latitude || !form.launch_longitude) { setSunset(null); return; }
    const fetchSunset = async () => {
      try {
        const res = await fetch(`https://api.sunrise-sunset.org/json?lat=${form.launch_latitude}&lng=${form.launch_longitude}&date=${form.event_date}&formatted=0`);
        const data = await res.json();
        if (data.status === 'OK' && data.results.sunset) {
          // Convert UTC sunset to local time using the event timezone or browser locale
          const utcSunset = new Date(data.results.sunset);
          const localSunset = timezone
            ? utcSunset.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone })
            : utcSunset.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          setSunset(localSunset);
        }
      } catch { setSunset(null); }
    };
    fetchSunset();
  }, [form.event_date, form.launch_latitude, form.launch_longitude, timezone]);

  const [tours, setTours] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const [selectedTourEvents, setSelectedTourEvents] = useState([]);
  const [creatingTour, setCreatingTour] = useState(false);
  const [tourSearch, setTourSearch] = useState('');

  const filteredTours = useMemo(() => {
    if (!tourSearch.trim()) return tours;
    const q = tourSearch.toLowerCase();
    return tours.filter(t =>
      t.name?.toLowerCase().includes(q) ||
      t.events?.some(e => e.name?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q))
    );
  }, [tours, tourSearch]);

  // Fetch tours for the tour picker
  useEffect(() => {
    const fetchTours = async () => {
      try {
        const data = await api.get('/tours');
        setTours(data || []);
      } catch {
        // silent fail
      }
    };
    fetchTours();
  }, []);

  // When editing, auto-show tour picker if event has a tour
  useEffect(() => {
    if (form.tour_id) setShowTour(true);
  }, [form.tour_id]);

  // Fetch events for the selected tour
  useEffect(() => {
    if (!form.tour_id) { setSelectedTourEvents([]); return; }
    const tour = tours.find(t => t.id === form.tour_id);
    if (tour?.events) {
      setSelectedTourEvents(tour.events.filter(e => e.id !== id));
    } else {
      setSelectedTourEvents([]);
    }
  }, [form.tour_id, tours, id]);

  // Compute this event's position in its tour (first / middle / last / only / none)
  const tourPosition = useMemo(() => {
    if (!form.tour_id || form.tour_id === 'new') return 'none';
    // selectedTourEvents excludes current event — add it back for sorting
    const allEvents = [...selectedTourEvents, { id, event_date: form.event_date }]
      .filter(e => e.event_date)
      .sort((a, b) => a.event_date.localeCompare(b.event_date));
    if (allEvents.length <= 1) return 'only';
    const idx = allEvents.findIndex(e => e.id === id);
    if (idx === 0) return 'first';
    if (idx === allEvents.length - 1) return 'last';
    return 'middle';
  }, [form.tour_id, form.event_date, selectedTourEvents, id]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  // Preview links (Creative tab). Row index is the identity — these are ordered
  // and unsaved rows have no id, so there is nothing else to key on.
  const setPreviewLink = (i, key, value) => setForm(f => ({
    ...f,
    preview_links: f.preview_links.map((l, j) => (j === i ? { ...l, [key]: value } : l)),
  }));
  const removePreviewLink = (i) => setForm(f => ({
    ...f,
    preview_links: f.preview_links.filter((_, j) => j !== i),
  }));
  // Index of the row whose URL field should take focus on its next render.
  const previewFocusRef = useRef(-1);
  const addPreviewLink = () => setForm(f => {
    previewFocusRef.current = f.preview_links.length;
    return { ...f, preview_links: [...f.preview_links, { label: '', url: '' }] };
  });

  // Moving the event date slides the (form-owned) end date with it by the same
  // whole-day delta, so a multi-day show keeps its duration and stays valid.
  // Ship dates are Transport-Scheduler-owned and read-only here — the backend
  // slides them to match on save (see events-router PATCH). UTC math (shiftDay)
  // avoids DST day-drift; an empty end date is left untouched.
  const handleEventDateChange = (newDate) => {
    const oldDate = form.event_date;
    if (!newDate || !oldDate) { set('event_date', newDate); return; }
    const delta = dayjs.utc(newDate).diff(dayjs.utc(oldDate), 'day');
    if (delta === 0) { set('event_date', newDate); return; }
    setForm(f => ({
      ...f,
      event_date: newDate,
      end_date: f.end_date ? shiftDay(f.end_date, delta) : f.end_date,
    }));
  };
  const setDetail = (key, value) => setForm(f => ({ ...f, details_json: { ...f.details_json, [key]: value } }));
  const resetSnapshots = () => {
    setFormSnapshot(JSON.parse(JSON.stringify(form)));
    setCrewSnapshot(JSON.parse(JSON.stringify(crew)));
    setExternalCrewSnapshot(JSON.parse(JSON.stringify(externalCrew)));
  };

  // Lock / unlock a tab or logistics section. When locking, count empty
  // fields in the scope and confirm with the user before proceeding — locking
  // blanks is allowed (covers N/A cases like mid-tour ship dates) but should
  // be deliberate. `scopeValues` is an array of the raw field values in the
  // locked scope so we can count blanks.
  const toggleLock = async (lockKey, label, scopeValues) => {
    const currentlyLocked = !!form[lockKey];
    if (currentlyLocked) {
      // Unlock — no confirmation needed.
      setForm(f => ({ ...f, [lockKey]: false }));
      return;
    }
    const blankCount = scopeValues.filter(v => !isFilled(v)).length;
    if (blankCount > 0) {
      const noun = blankCount === 1 ? 'field is' : 'fields are';
      const ok = await confirm({
        message: `${blankCount} ${label} ${noun} left blank. Lock anyway?`,
        confirmLabel: 'Lock anyway',
      });
      if (!ok) return;
    }
    setForm(f => ({ ...f, [lockKey]: true }));
  };

  // Computed fields
  const lsDroneCount = Math.max(0, form.total_drone_count - form.pyro_drone_count);
  const buffLs = lsDroneCount + Math.ceil(lsDroneCount * form.buffer_percentage / 100);
  const buffPyro = form.pyro_drone_count + Math.ceil(form.pyro_drone_count * form.buffer_percentage / 100);
  const totalBuffered = buffLs + buffPyro;
  const batteryCount = totalBuffered * (form.is_double_show ? 2 : 1);

  const getOverbookWarning = () => {
    if (!availability || totalBuffered <= availability.availableDrones) return null;
    const z = availability.committedByZone || {};
    const over = totalBuffered - availability.availableDrones;
    // Determine worst zone contributing to the conflict
    if (z.red > 0) return {
      severity: 'red',
      message: `⛔ CRITICAL OVERBOOKING\n\nYou are ${over} drones over capacity.\n\n${z.red} drones are committed to events within 1-2 days — these are almost certainly still in transit and CANNOT be redirected without extreme measures.\n\nThis WILL cause a show failure unless you can source additional drones immediately.\n\nAre you absolutely sure you want to proceed?`
    };
    if (z.orange > 0) return {
      severity: 'orange',
      message: `⚠️ SERIOUS CONFLICT\n\nYou are ${over} drones over capacity.\n\n${z.orange} drones are committed to events within 3-4 days — these are likely still in transit and would need direct shipping between sites to make it.\n\nThis will require significant coordination and rush logistics.\n\nDo you want to proceed anyway?`
    };
    if (z.yellow > 0) return {
      severity: 'yellow',
      message: `⚠ Scheduling Conflict\n\nYou are ${over} drones over capacity.\n\n${z.yellow} drones are committed to events within 5-6 days — these may still be in transit and will require special coordination.\n\nProceed with caution. Continue?`
    };
    return {
      severity: 'green',
      message: `You are ${over} drones over capacity, but all conflicts are 7+ days out. Should be fine — just a heads up.\n\nContinue?`
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.event_date) {
      toast.error('Name and date are required');
      return;
    }

    // External crew validation — name + valid email required for every row.
    // Surface row position so the producer can find the bad entry without scrolling.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (let i = 0; i < externalCrew.length; i++) {
      const ec = externalCrew[i];
      const name = (ec.name || '').trim();
      const email = (ec.email || '').trim();
      if (!name) {
        toast.error(`External crew #${i + 1}: name is required`);
        return;
      }
      if (!email || !EMAIL_RE.test(email)) {
        toast.error(`External crew #${i + 1} (${name}): a valid email is required`);
        return;
      }
    }

    const warning = getOverbookWarning();
    if (warning) {
      const proceed = await confirm({
        message: warning.message,
        confirmLabel: 'Proceed',
        tone: warning.severity === 'red' ? 'danger' : 'default',
      });
      if (!proceed) return;
    }

    setSaving(true);
    try {
      // If user selected "new" tour, create it first
      let tourId = form.tour_id;
      if (tourId === 'new') {
        setCreatingTour(true);
        try {
          const tourName = `Tour — ${dayjs(form.event_date).format('MMM YYYY')}`;
          const newTour = await api.post('/tours', { body: { name: tourName, event_ids: [] } });
          tourId = newTour.id;
        } catch (err) {
          toast.error('Failed to create tour');
          setSaving(false);
          setCreatingTour(false);
          return;
        }
        setCreatingTour(false);
      }

      const pyro = parseInt(form.pyro_drone_count) || 0;
      const total = parseInt(form.total_drone_count) || 0;
      const { total_drone_count, ...rest } = form;
      const body = {
        ...rest,
        end_date: form.end_date || null,
        rain_date: form.rain_date || null,
        show_time: form.show_time || null,
        // User chose "clear on untoggle" — if they uncheck is_double_show
        // we null the DB column, not merely hide the input. This keeps the
        // data model honest with what the UI is showing.
        second_show_time: (form.is_double_show && form.second_show_time) ? form.second_show_time : null,
        show_height_ft: form.show_height_ft ? parseInt(form.show_height_ft) : null,
        // Blank rows are what an untouched "Add preview link" leaves behind —
        // drop them here so an empty row never round-trips as a saved link.
        preview_links: form.preview_links
          .map(l => ({ label: (l.label || '').trim(), url: (l.url || '').trim() }))
          .filter(l => l.url),
        site_map_url: form.site_map_url || null,
        contacts: form.contacts.length > 0 ? form.contacts : [],
        launch_latitude: form.launch_latitude || null,
        launch_longitude: form.launch_longitude || null,
        audience_latitude: showAudience ? (form.audience_latitude || null) : null,
        audience_longitude: showAudience ? (form.audience_longitude || null) : null,
        surface_type: form.surface_type || null,
        timezone: form.timezone || null,
        ls_drone_count: Math.max(0, total - pyro),
        pyro_drone_count: pyro,
        buffer_percentage: parseInt(form.buffer_percentage) || 10,
        tour_id: tourId || null,
        // Ship dates are read-only here (managed by drag in the Transport
        // Scheduler) and echoed back unchanged — EXCEPT when the event date
        // moves, where handleEventDateChange slides them by the same delta so
        // the save stays valid without a separate scheduler edit.
        tracking_number_out: form.tracking_number_out || null,
        tracking_number_return: form.tracking_number_return || null,
        details_json: form.details_json,
        ...extractLockFlags(form),
      };

      // Include crew assignments in save
      body.crew = crew.map(c => ({ user_id: c.user_id, role: c.role, notes: c.notes }));

      // External crew — id is omitted on new rows (server will generate)
      body.external_crew = externalCrew.map(c => ({
        ...(c.id ? { id: c.id } : {}),
        name: c.name.trim(),
        email: c.email.trim(),
        phone: (c.phone || '').trim(),
        notes: c.notes || '',
      }));

      // Ship dates + transport mode are owned by the Transport Scheduler and shown
      // read-only here — the form is not their editor, so never write them back
      // (doing so would clobber a concurrent Scheduler edit). When the event date
      // moves, the backend slides the stored ship dates to match. On create there's
      // no event yet, so let the POST seed its defaults.
      if (isEdit) {
        delete body.ship_out_date;
        delete body.ship_back_date;
        delete body.transport_mode;
        delete body.transport_arranged;
      }

      if (isEdit) {
        const lockSessionId = lockCtxRef.current?.sessionId;
        const updated = await api.patch(`/events/${id}`, {
          body,
          headers: lockSessionId ? { 'X-Edit-Lock-Session': lockSessionId } : undefined,
        });
        resetSnapshots();
        toast.success('Event saved');

        // Backend cascade: a coord change with a previously linked site map
        // auto-nulls the URL. Surface a prompt so the user can update producer
        // with the new coords.
        if (updated?.site_map_unlinked?.prior_url) {
          const open = await confirm({
            title: 'Site map link cleared',
            message: 'The launch coordinates changed, and the producer site map was drawn at the old location, so it is no longer accurate.\n\n'
              + 'Open producer in a new tab to update its coordinates, '
              + 'then paste the share URL back here on your next save.',
            confirmLabel: 'Open producer',
          });
          if (open) {
            window.open(updated.site_map_unlinked.prior_url, '_blank', 'noopener,noreferrer');
          }
          toast.warning('Site map unlinked');
        }

        // Save exits edit mode, landing on the tab that was open so the round
        // trip stays in one place. replace, not push: the editor is finished
        // with, and pushing would put Back on a re-entry into it that also
        // re-acquires the edit lock.
        leavingAfterSaveRef.current = true;
        history.replace(`/events/${id}?tab=${activeTab}`);
      } else {
        const created = await api.post('/events', { body });
        resetSnapshots();
        toast.success('Event created');
        leavingAfterSaveRef.current = true;
        history.push(`/events/${created.id}`);
      }
    } catch (error) {
      if (error?.status === 409 && error?.data?.error?.code === 'edit_lock_lost') {
        // Lock expired silently while the tab was idle. Trigger the guard's
        // recovery modal — it will preserve the user's typed changes for copy
        // and then reload the page so they see whatever the next editor saved.
        lockCtxRef.current?.signalLockLost();
      } else {
        toast.error(error.message || 'Failed to save event');
      }
    } finally {
      // A successful save has already navigated away, so this component is
      // unmounted by now and setting state on it is a React warning for no
      // benefit. Only the paths that stay on the form need the button back.
      if (!leavingAfterSaveRef.current) setSaving(false);
    }
  };

  if (loading) return <LogoSpinner label="Loading event" />;

  const enoughDrones = availability && totalBuffered <= availability.availableDrones;
  const enoughBatteries = availability && batteryCount <= availability.availableBatteries;

  const cardCls = "bg-surface-container-low rounded-md p-5";
  const sectionHdr = (label) => (
    <h3 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold flex items-center gap-2">
      <span className="w-1 h-3.5 rounded-full bg-primary/60 inline-block" />
      {label}
    </h3>
  );

  // Reusable lock button. Renders "Lock In" (gray) when unlocked, "Locked"
  // (green) when locked. Click to toggle. Warns before locking blank scopes.
  const LockButton = ({ lockKey, label, scopeValues }) => {
    const locked = !!form[lockKey];
    return (
      <Tooltip label={locked ? `Click to unlock ${label}` : `Lock in ${label}`}>
        <Chip
          pressed={locked}
          hex={CATEGORY.emerald.ink.hex}
          onClick={() => toggleLock(lockKey, label, scopeValues)}
          leading={locked ? (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm-3 8V6a3 3 0 016 0v3H9z"/></svg>
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
          )}
        >
          {locked ? 'Locked' : 'Lock In'}
        </Chip>
      </Tooltip>
    );
  };

  // Spread-ready props for wrappers that visually dim + fully block interaction
  // inside locked sections. Uses the HTML `inert` attribute — blocks mouse,
  // keyboard, AND screen-reader interaction with the subtree (pointer-events-none
  // alone leaves keyboard Tab working). Lock button must live OUTSIDE this
  // wrapper so Unlock stays clickable.
  // Usage: <div {...lockedWrapProps(form.locked_X, 'space-y-4')}>
  const lockedWrapProps = (isLocked, extraClass = '') => {
    if (!isLocked) return { className: extraClass };
    const className = `opacity-60 select-none ${extraClass}`.trim();
    return { inert: '', className, 'aria-disabled': 'true' };
  };

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto">
      <BackLink
        to={isEdit ? `/events/${id}?tab=${activeTab}` : '/events'}
        label={isEdit ? 'Event' : 'Events'}
        className="mb-5"
      />

      <EditLockGuard resourceType="event" resourceId={id} enabled={isEdit} resourceLabel="This event">

      <LockSessionBridge ctxRef={lockCtxRef} formSnapshot={formSnapshot} form={form} crew={crew} externalCrew={externalCrew} isEdit={isEdit} />

      {/* Header Card */}
      <div className="bg-gray-800 border border-gray-700/60 rounded-md overflow-hidden relative mb-6" style={{ boxShadow: '0 0 40px rgba(255,204,51,0.03), 0 1px 3px rgba(0,0,0,.4)' }}>
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        <div className="px-5 pt-5 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-primary/70 uppercase tracking-widest font-semibold">{isEdit ? 'Editing' : 'New Event'}</span>
                <Appear show={anyDirty}><Tag tone="warn" size="sm">Unsaved changes</Tag></Appear>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight truncate mt-1">
                {form.name || <span className="text-on-surface-disabled italic font-normal">Untitled Event</span>}
              </h1>
              {isEdit && (
                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                  {form.status && (
                    <Tag
                      {...({ confirmed: { hex: CATEGORY.blue.ink.hex }, active: { hex: CATEGORY.green.ink.hex }, completed: { hex: CATEGORY.emerald.ink.hex }, cancelled: { hex: CATEGORY.red.ink.hex }, packing: { hex: CATEGORY.purple.ink.hex } }[form.status] || { tone: 'neutral' })}
                      className="capitalize"
                    >{form.status}</Tag>
                  )}
                  <EventAirspaceWidget eventId={id} onClick={() => setAirspaceModalOpen(true)} />
                </div>
              )}
            </div>
            <div className="hidden sm:flex flex-col items-end flex-shrink-0">
              {form.event_date && (
                <>
                  <span className="text-xs text-on-surface-variant uppercase tracking-wide">Event Date</span>
                  <span className="text-lg font-semibold text-white mt-0.5">
                    {dayjs(form.event_date).format('MMM D, YYYY')}
                  </span>
                  {form.end_date && form.end_date !== form.event_date && (
                    <span className="text-xs text-on-surface-variant mt-0.5">
                      &rarr; {dayjs(form.end_date).format('MMM D, YYYY')}
                    </span>
                  )}
                </>
              )}
              <div className="flex items-center gap-2 mt-3">
                <Link to={isEdit ? `/events/${id}?tab=${activeTab}` : '/events'}
                  className={buttonClass('tonal', 'md')}>
                  Cancel
                </Link>
                <button type="submit" form="event-form" disabled={saving}
                  className={buttonClass('filled', 'md')}>
                  {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Two navigations must pass through this guard unchallenged, and neither
          is a user abandoning an edit.

          1. Switching tabs. setActiveTab history.replace()s the ?tab= param,
             and history blocks replace exactly as it blocks push — without the
             same-pathname rule, every tab click on a dirty form would raise
             "leave without saving?". Staying on the route is not leaving it.
          2. The save completing. <Prompt> blocks synchronously inside
             history.replace(), before React has re-rendered with the post-save
             snapshot. On React 17's legacy root the setState in
             resetSnapshots() flushes immediately so anyDirty is already false
             by then — but that is a batching detail, not a guarantee, and a
             React 18 createRoot upgrade would silently turn every save into a
             dialog. The ref states the intent instead of inferring it from
             scheduling. (Returning true from `message` allows the transition;
             see history/cjs/history.js confirmTransitionTo.) */}
      <Prompt
        when={anyDirty}
        message={(nextLocation) =>
          nextLocation.pathname === location.pathname
          || leavingAfterSaveRef.current
          || 'You have unsaved changes. Leave without saving?'}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <form id="event-form" onSubmit={handleSubmit} onKeyDown={e => { if (e.key === 'Enter' && !e.target.isContentEditable && e.target.tagName !== 'TEXTAREA') e.preventDefault(); }} className="lg:col-span-2 space-y-5">

          {/* Tab Bar */}
          <Tabs
            fill
            size="lg"
            label="Event sections"
            value={activeTab}
            onChange={setActiveTab}
            tabs={[
              ['main', 'Main'],
              ['location', 'Location'],
              ['creative', 'Creative'],
              ['logistics', 'Logistics'],
              ['contacts', 'Contacts'],
            ].map(([key, label]) => ({
              key,
              label,
              dirty: isTabDirty(key),
              ring: {
                filled: tabCompletion[key]?.filled || 0,
                locked: tabCompletion[key]?.locked || 0,
                total: tabCompletion[key]?.total || 1,
              },
            }))}
          />

          {/* ═══════════════════ MAIN TAB ═══════════════════ */}
          {activeTab === 'main' && (<FadeIn key="main" className="space-y-5">
          <div className="flex items-center justify-center -mb-2">
            <LockButton lockKey="locked_main" label="Main" scopeValues={lockScopes.locked_main} />
          </div>

          {/* Lifecycle card — Status and Tour stay editable even when Main is locked.
              Status moves through the workflow (pending→confirmed→packing→…) and
              tour membership can shift as producers reorganize, so both are
              deliberately outside the lock wrapper. */}
          <div className={`${cardCls} space-y-4`}>
            <div>
              {sectionHdr('Status')}
              <Select
                label="Status"
                className="mt-2"
                value={form.status}
                onChange={key => set('status', key)}
                options={['pending', 'confirmed', 'packing', 'active', 'cancelled'].map(s => ({ key: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
              />
            </div>
            <div>
              <ChipToggle
                checked={showTour}
                onChange={e => { setShowTour(e.target.checked); if (!e.target.checked) set('tour_id', null); }}
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.115 5.19l.319 1.913A6 6 0 008.11 10.36L9.75 12l-.387.775c-.217.433-.132.956.21 1.298l1.348 1.348c.21.21.329.497.329.795v1.089c0 .426.24.815.622 1.006l.153.076c.433.217.956.132 1.298-.21l.723-.723a8.7 8.7 0 002.288-4.042 1.087 1.087 0 00-.358-1.099l-1.33-1.108c-.251-.21-.582-.299-.905-.245l-1.17.195a1.125 1.125 0 01-.98-.314l-.295-.295a1.125 1.125 0 010-1.591l.13-.132a1.125 1.125 0 011.3-.21l.603.302a.809.809 0 001.086-1.086L14.25 7.5l1.256-.837a4.5 4.5 0 001.528-1.732l.146-.292M6.115 5.19A9 9 0 1017.18 4.64M6.115 5.19A8.965 8.965 0 0112 3c1.929 0 3.716.607 5.18 1.64" /></svg>}>
                  Add to Tour
              </ChipToggle>
              <Reveal show={showTour}>
                <div className="space-y-3 px-1 pb-1 pt-3">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-on-surface-variant pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="m21 21-4.35-4.35"/></svg>
                    <Field type="text" value={tourSearch} onChange={e => setTourSearch(e.target.value)} placeholder="Search tours..." className="pl-8" />
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto rounded-md border border-gray-700/40 bg-gray-900/50 p-1">
                    {filteredTours.map(t => (
                      <button key={t.id} type="button" onClick={() => { set('tour_id', t.id); setTourSearch(''); }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${form.tour_id === t.id ? 'bg-primary/10 ring-1 ring-inset ring-primary/30 text-white' : 'text-on-surface hover:bg-surface-container'}`}>
                        <span className="font-medium">{t.name}</span>
                        <span className="text-on-surface-variant ml-2 text-xs">{t.events?.length || 0} events{t.events?.length > 0 && ` — ${dayjs(t.events[0]?.event_date).format('MMM D')}–${dayjs(t.events[t.events.length - 1]?.event_date).format('MMM D')}`}</span>
                      </button>
                    ))}
                    <button type="button" onClick={() => { set('tour_id', 'new'); setTourSearch(''); }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${form.tour_id === 'new' ? 'bg-primary/10 ring-1 ring-inset ring-primary/30 text-primary-ink' : 'text-primary/70 hover:bg-surface-container'}`}>
                      + Create New Tour
                    </button>
                    {filteredTours.length === 0 && tourSearch.trim() && (
                      <p className="text-on-surface-variant text-xs py-2 text-center">No tours match &ldquo;{tourSearch}&rdquo;</p>
                    )}
                  </div>
                  <Reveal show={!!form.tour_id && form.tour_id !== 'new'}>
                    <div className="p-1">
                      <button type="button" onClick={() => set('tour_id', null)} className={buttonClass('quiet', 'sm')}>Remove from tour</button>
                    </div>
                  </Reveal>
                  <Reveal show={selectedTourEvents.length > 0}>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTourEvents.map(e => (
                        <Tag key={e.id} hex={CATEGORY.cyan.ink.hex}>
                          <span className="inline-flex items-center gap-1.5">{e.name}<span className="opacity-60">{dayjs(e.event_date).format('M/D')}</span></span>
                        </Tag>
                      ))}
                    </div>
                  </Reveal>
                  <Reveal show={form.tour_id === 'new'}>
                    <p className="m-0 text-xs text-on-surface-variant">A new tour will be created when you save.</p>
                  </Reveal>
                </div>
              </Reveal>
            </div>
          </div>

          <div {...lockedWrapProps(form.locked_main)}>
          {/* Event Details Card */}
          <div className={`${cardCls} space-y-4`}>
            <div>
              <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">Event Name *</label>
              <Field type="text" value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">Event Date *</label>
                <Field type="date" value={form.event_date} onChange={e => handleEventDateChange(e.target.value)} required />
              </div>
              <div>
                <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">
                  {form.is_double_show ? '1st Show Time' : 'Show Time'}
                  {timezone && <span className="text-on-surface-variant ml-1 normal-case tracking-normal font-normal">({timezone.replace(/_/g, ' ')})</span>}
                </label>
                <Field type="time" value={form.show_time} onChange={e => {
                  const t = e.target.value;
                  if (t) {
                    const [h, m] = t.split(':').map(Number);
                    const mins = h * 60 + m;
                    const fmt = (total) => { const hh = Math.floor(((total % 1440) + 1440) % 1440 / 60); const mm = ((total % 1440) + 1440) % 1440 % 60; return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`; };
                    setForm(f => ({
                      ...f, show_time: t,
                      details_json: {
                        ...f.details_json,
                        perimeter_secured: fmt(mins - 15),
                        perimeter_lifted: fmt(mins + 15),
                      }
                    }));
                    setAutoFilledFields(['perimeter_secured', 'perimeter_lifted']);
                    setTimeout(() => setAutoFilledFields([]), 1500);
                  } else {
                    set('show_time', '');
                  }
                }} />
              </div>
            </div>

            {/* Second show time — only relevant when is_double_show is on.
                Unlike the first show time it does NOT auto-fill the perimeter
                fields: the safety perimeter is per-event, not per-show. */}
            <Reveal show={!!form.is_double_show}>
              <div className="w-1/2 px-1 py-1">
                <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1.5">
                  2nd Show Time
                  {timezone && <span className="text-on-surface-variant ml-1 normal-case tracking-normal font-normal">({timezone.replace(/_/g, ' ')})</span>}
                </label>
                <Field
                  type="time"
                  value={form.second_show_time}
                  onChange={e => set('second_show_time', e.target.value)}
                />
              </div>
            </Reveal>
            <div className="flex gap-3">
              <ChipToggle
                checked={!!form.end_date}
                onChange={e => set('end_date', e.target.checked ? form.event_date : '')}
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>}>
                  Multi-day
              </ChipToggle>
              <ChipToggle
                checked={form.rain_date !== ''}
                onChange={e => set('rain_date', e.target.checked ? ' ' : '')}
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" /></svg>}>
                  Rain Date
              </ChipToggle>
            </div>
            <Reveal show={form.end_date !== ''}>
              <div className="w-1/2 px-1 py-1">
                <label className="block text-xs text-on-surface-variant mb-1">End Date</label>
                <Field type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} min={form.event_date} />
              </div>
            </Reveal>
            <Reveal show={form.rain_date !== ''}>
              <div className="w-1/2 px-1 py-1">
                <label className="block text-xs text-on-surface-variant mb-1">Rain Date</label>
                <Field type="date" value={form.rain_date === ' ' ? '' : form.rain_date} onChange={e => set('rain_date', e.target.value)} />
              </div>
            </Reveal>

            {/* Drone Allocation */}
            <div className="border-t border-gray-700/50 pt-5 space-y-5">
            <div>
              {sectionHdr('Drone Allocation')}
              <div className="grid grid-cols-3 gap-3 mt-2.5">
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1">Total Drones</label>
                  <Field type="number" min="0" value={form.total_drone_count} onChange={e => set('total_drone_count', parseInt(e.target.value) || 0)} />
                </div>
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1">Of Which Pyro</label>
                  <Field type="number" min="0" max={form.total_drone_count} value={form.pyro_drone_count}
                    onChange={e => set('pyro_drone_count', Math.min(parseInt(e.target.value) || 0, form.total_drone_count))} />
                </div>
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1">Buffer %</label>
                  <Field type="number" min="0" max="100" value={form.buffer_percentage} onChange={e => set('buffer_percentage', parseInt(e.target.value) || 0)} />
                </div>
              </div>
              {form.total_drone_count > 0 && (
                <div className="text-xs text-on-surface-variant bg-gray-900/40 rounded-md px-3 py-2 mt-2">
                  {lsDroneCount} Standard + {form.pyro_drone_count} Pyro = {form.total_drone_count} drones
                </div>
              )}
            </div>

            {/* Options — Double Show only; Tour lives in the Lifecycle card above */}
            <div className="flex flex-wrap gap-2">
              <ChipToggle
                checked={form.is_double_show}
                onChange={e => {
                  const checked = e.target.checked;
                  // Clear second_show_time on untoggle per producer request —
                  // avoids stale values surprising them on re-enable later.
                  setForm(f => ({ ...f, is_double_show: checked, ...(checked ? {} : { second_show_time: '' }) }));
                }}
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3" /></svg>}>
                  Double Show
              </ChipToggle>
            </div>
            </div>
          </div>

          {/* Run Of Show Card */}
          <div className={`${cardCls} space-y-4`}>
            {sectionHdr('Run of Show')}
            {/* Sunset banner */}
            <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-md text-sm ${sunset ? 'bg-yellow-400/[0.07] border border-yellow-400/20' : 'bg-gray-900/40 border border-gray-700/40'}`}>
              <svg className={`w-4 h-4 flex-shrink-0 ${sunset ? 'text-yellow-400' : 'text-on-surface-disabled'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
              </svg>
              <FadeIn key={sunset ? 'sunset' : 'none'}>
                {sunset
                  ? <span className="text-yellow-400/90 font-medium">Sunset: {sunset}{timezone && <span className="text-yellow-400/80 ml-1 font-normal text-xs">({timezone.replace(/_/g, ' ')})</span>}</span>
                  : <span className="text-on-surface-variant italic">{form.event_date ? 'Choose a location to show sunset time' : 'Choose a date and location to show sunset time'}</span>
                }
              </FadeIn>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                ['load_in', 'Load In'],
                ['sound_check', 'Sound Check'],
                ['four_corners', 'Four-Corners Test'],
                ['perimeter_secured', 'Perimeter Secured'],
                ['perimeter_lifted', 'Perimeter Lifted'],
                ['load_out', 'Load Out'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs text-on-surface-variant mb-1">{label}</label>
                  <Field type="time" value={form.details_json[key]} onChange={e => setDetail(key, e.target.value)}
                    className={autoFilledFields.includes(key) ? 'keep-ring ring-2 ring-primary/60' : ''} />
                </div>
              ))}
            </div>
          </div>

          {/* Notes Card */}
          <div className={`${cardCls} space-y-3`}>
            {sectionHdr('Notes')}
            <TextArea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
          </div>
          </div>
          </FadeIn>)}

          {/* ═══════════════════ LOCATION TAB ═══════════════════ */}
          {activeTab === 'location' && (<FadeIn key="location" className="space-y-5">
          <div className="flex items-center justify-center -mb-2">
            <LockButton lockKey="locked_location" label="Location" scopeValues={lockScopes.locked_location} />
          </div>
          <div {...lockedWrapProps(form.locked_location)}>
          <div className={cardCls}>
            <div className="flex items-center gap-4 mb-4">
              {sectionHdr('Location')}
              {(sunset || form.event_date) && (
                <FadeIn key={sunset ? 'sunset' : 'none'}>
                  {sunset
                    ? <Tag hex={CATEGORY.yellow.ink.hex} size="sm">Sunset: {sunset}</Tag>
                    : <span className="text-xs text-on-surface-variant italic">Set coordinates to see sunset</span>}
                </FadeIn>
              )}
              <ChipToggle
                size="sm"
                className="ml-auto"
                checked={showAudience}
                onChange={e => {
                  setShowAudience(e.target.checked);
                  if (!e.target.checked) setForm(f => ({ ...f, audience_latitude: null, audience_longitude: null }));
                }}
              >
                Audience Location
              </ChipToggle>
            </div>
            <LocationPicker
              location={form.location}
              onChange={(loc) => set('location', loc)}
              launchLat={form.launch_latitude}
              launchLng={form.launch_longitude}
              onLaunchChange={(lat, lng) => setForm(f => ({ ...f, launch_latitude: lat, launch_longitude: lng }))}
              audienceLat={form.audience_latitude}
              audienceLng={form.audience_longitude}
              onAudienceChange={(lat, lng) => setForm(f => ({ ...f, audience_latitude: lat, audience_longitude: lng }))}
              showAudience={showAudience}
            />
            <AirspaceAdvisoryPanel
              lat={form.launch_latitude}
              lng={form.launch_longitude}
              eventDate={form.event_date}
            />
          </div>

          {/* Site Details (moved from Show Details) */}
          <div className={`${cardCls} space-y-4`}>
            {sectionHdr('Site Details')}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-on-surface-variant mb-1">Show Height (ft)</label>
                <Field type="number" min="0" value={form.show_height_ft} onChange={e => set('show_height_ft', e.target.value)} placeholder="e.g. 600" />
              </div>
              <div>
                <label className="block text-xs text-on-surface-variant mb-1">Surface Type</label>
                <Select
                  label="Surface type"
                  value={form.surface_type}
                  onChange={key => set('surface_type', key)}
                  options={[
                    { key: '', label: '--' },
                    { key: 'grass', label: 'Grass' },
                    { key: 'concrete', label: 'Concrete' },
                    { key: 'asphalt', label: 'Asphalt' },
                    { key: 'turf', label: 'Turf' },
                    { key: 'dirt', label: 'Dirt' },
                    { key: 'other', label: 'Other' },
                  ]}
                />
              </div>
            </div>
            {/* Site Map URL — links a producer share page to this event. */}
            <div>
              <label className="block text-xs text-on-surface-variant mb-1">Site Map URL</label>
              <Field
                type="url"
                value={form.site_map_url}
                onChange={e => set('site_map_url', e.target.value)}
                placeholder="https://producer.fireflystudio.app/share/..."
                error={!!(form.site_map_url && !parseShareUrl(form.site_map_url))}
              />
              <SiteMapPill share={siteMapShare} onClear={() => set('site_map_url', '')} />
            </div>
          </div>
          </div>
          </FadeIn>)}

          {/* ═══════════════════ CREATIVE TAB ═══════════════════ */}
          {activeTab === 'creative' && (<FadeIn key="creative" className="space-y-5">
          <div className="flex items-center justify-center -mb-2">
            <LockButton lockKey="locked_creative" label="Creative" scopeValues={lockScopes.locked_creative} />
          </div>
          <div {...lockedWrapProps(form.locked_creative)}>
          {/* Preview Links */}
          <div className={cardCls}>
            <div className="flex items-center justify-between mb-4">
              {sectionHdr('Preview Links')}
              <button type="button" onClick={addPreviewLink}
                className={buttonClass('quiet', 'sm')}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                Preview Link
              </button>
            </div>
            {form.preview_links.length === 0 && (
              <EmptyState variant="area" className="!py-3">
                No preview links yet. Add one per show if this night runs two different shows.
              </EmptyState>
            )}
            <div className="space-y-2.5">
              {form.preview_links.map((link, i) => (
                <div key={i} className="bg-gray-900/60 border border-gray-700/40 rounded-md p-3.5">
                  <div className="flex gap-2.5 items-end">
                    <div className="w-32 flex-shrink-0">
                      <label className="block text-xs text-on-surface-variant mb-1">Label</label>
                      <Field type="text" value={link.label} maxLength={60}
                        onChange={e => setPreviewLink(i, 'label', e.target.value)}
                        placeholder={`Show ${i + 1}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs text-on-surface-variant mb-1">URL</label>
                      {/* Callback ref, not an effect: the row that was just added
                          focuses itself as it mounts, so typing continues without
                          reaching for the mouse. */}
                      <Field type="url" value={link.url} maxLength={500}
                        ref={el => {
                          if (el && previewFocusRef.current === i) {
                            previewFocusRef.current = -1;
                            el.focus();
                          }
                        }}
                        onChange={e => setPreviewLink(i, 'url', e.target.value)}
                        placeholder="https://preview.fireflystudio.app/..." />
                    </div>
                    <Tooltip label={`Remove preview link ${i + 1}`}><IconButton label={`Remove preview link ${i + 1}`} onClick={() => removePreviewLink(i)}>
                      <IconClose />
                    </IconButton></Tooltip>
                  </div>
                  {link.url && (
                    <a href={link.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary hover:text-primary/80 transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-4.5-6h6m0 0v6m0-6L9.75 14.25" /></svg>
                      Open Preview
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Music & Show Info */}
          <div className={`${cardCls} space-y-3`}>
            {sectionHdr('Music & Show Info')}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-on-surface-variant mb-1">Music</label>
                <Field size="sm" type="text" value={form.details_json.music} onChange={e => setDetail('music', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-on-surface-variant mb-1">Time Machine</label>
                <Field size="sm" type="text" value={form.details_json.time_machine} onChange={e => setDetail('time_machine', e.target.value)} />
              </div>
            </div>
            <div>
              <label className="block text-xs text-on-surface-variant mb-1">Music Link Code</label>
              <Field size="sm" type="text" value={form.details_json.music_link_code} onChange={e => setDetail('music_link_code', e.target.value)} />
            </div>
          </div>

          {/* Creative */}
          <div className={`${cardCls} space-y-3`}>
            {sectionHdr('Creative')}
            <RichTextArea value={form.details_json.creative} onChange={v => setDetail('creative', v)} rows={5} placeholder="Design notes, animation concepts, choreography details..." />
          </div>
          </div>
          </FadeIn>)}

          {/* ═══════════════════ LOGISTICS TAB ═══════════════════ */}
          {activeTab === 'logistics' && (<FadeIn key="logistics" className="space-y-5">

          {/* Crew Assignments */}
          <div className={cardCls}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                {sectionHdr('Crew')}
                {(crew.length + externalCrew.length) > 0 && (
                  <Tag tone="accent" size="sm">
                    <span className="inline-flex items-center gap-1.5">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.053M18 10.5a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zM12.75 6.75a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {crew.length + externalCrew.length}
                    </span>
                  </Tag>
                )}
              </div>
              <LockButton lockKey="locked_logistics_crew" label="Crew" scopeValues={lockScopes.locked_logistics_crew} />
            </div>
            <div {...lockedWrapProps(form.locked_logistics_crew)}>

            {/* Legacy crew text banner (accordion) */}
            {form.details_json.crew && (
              <div className="mb-4 border border-amber-500/20 bg-amber-500/5 rounded-md overflow-hidden">
                <button type="button" onClick={() => setShowLegacyCrew(prev => !prev)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs text-amber-400/80 hover:bg-amber-500/10 transition-colors">
                  <span className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                    Legacy crew notes
                  </span>
                  <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${showLegacyCrew ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showLegacyCrew && (
                  <div className="px-3 pb-3">
                    <div className="text-sm text-on-surface-variant border-l-2 border-amber-500/30 pl-3 py-1.5 whitespace-pre-wrap leading-relaxed">{form.details_json.crew.replace(/<[^>]*>/g, '')}</div>
                    <button type="button" onClick={() => { setDetail('crew', ''); setShowLegacyCrew(false); }}
                      className={`${buttonClass('danger-quiet', 'sm')} mt-2.5`}>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                      Clear legacy notes
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Crew picker */}
            <div className="mb-4">
              <UserPicker
                users={allUsers}
                excludeIds={crew.map(c => c.user_id)}
                onSelect={(user) => setCrew(prev => [...prev, { user_id: user.id, role: '', notes: '', firstname: user.firstname, lastname: user.lastname }])}
              />
            </div>

            {/* Crew cards */}
            {crew.length === 0 && (
              <EmptyState variant="area" title="No crew assigned yet">
                Search above to add team members
              </EmptyState>
            )}
            <div className="space-y-2">
              {crew.map((member, i) => (
                <div key={member.user_id} className="group bg-gray-900/50 border border-gray-700/30 rounded-md px-3.5 py-3 hover:border-[rgb(70_70_70/0.4)] transition-all duration-150">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[13px] text-white font-medium tracking-tight">{member.firstname} {member.lastname}</span>
                    <Tooltip label={`Remove ${member.firstname} ${member.lastname}`}><IconButton label={`Remove ${member.firstname} ${member.lastname}`} size="sm" onClick={() => setCrew(prev => prev.filter((_, j) => j !== i))}
                      className="opacity-0 group-hover:opacity-100">
                      <IconClose />
                    </IconButton></Tooltip>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field size="sm" type="text" value={member.role} onChange={e => { const u = [...crew]; u[i] = { ...u[i], role: e.target.value }; setCrew(u); }} placeholder="Role — e.g. PIC, Spotter" />
                    <Field size="sm" type="text" value={member.notes} onChange={e => { const u = [...crew]; u[i] = { ...u[i], notes: e.target.value }; setCrew(u); }} placeholder="Notes" />
                  </div>
                </div>
              ))}
            </div>

            {/* External crew — non-Firefleet contractors / client-side helpers.
                Each row gets a personalized email on add and on notes change. */}
            <div className="mt-4 pt-4 border-t border-outline-variant">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <h4 className="text-[11px] text-on-surface-variant uppercase tracking-wider font-semibold">External Crew</h4>
                  {externalCrew.length > 0 && (
                    <Tag hex={CATEGORY.orange.ink.hex} variant="status" size="sm" className="tabular-nums">
                      {externalCrew.length}
                    </Tag>
                  )}
                </div>
                <button type="button"
                  onClick={() => setExternalCrew(prev => [...prev, { name: '', email: '', phone: '', notes: '' }])}
                  className={buttonClass('quiet', 'sm')}>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                  External Crew
                </button>
              </div>

              {externalCrew.length === 0 ? (
                <p className="text-[11px] text-on-surface-variant italic">Add contractors or client-side helpers — they'll be emailed event details, your notes, and the onsite producer's contact.</p>
              ) : (
                <div className="space-y-2">
                  {externalCrew.map((ec, i) => (
                    <div key={ec.id || `new-${i}`} className="group bg-gray-900/50 border border-gray-700/30 rounded-md px-3.5 py-3 hover:border-[rgb(70_70_70/0.4)] transition-all duration-150">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-orange-300/70">External</span>
                        <Tooltip label="Remove external crew member"><IconButton label="Remove external crew member" size="sm"
                          onClick={() => setExternalCrew(prev => prev.filter((_, j) => j !== i))}
                          className="opacity-0 group-hover:opacity-100">
                          <IconClose />
                        </IconButton></Tooltip>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <Field size="sm" type="text" required value={ec.name}
                          onChange={e => { const u = [...externalCrew]; u[i] = { ...u[i], name: e.target.value }; setExternalCrew(u); }}
                          placeholder="Name *" />
                        <Field size="sm" type="email" required value={ec.email}
                          onChange={e => { const u = [...externalCrew]; u[i] = { ...u[i], email: e.target.value }; setExternalCrew(u); }}
                          placeholder="Email *" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field size="sm" type="tel" value={ec.phone}
                          onChange={e => { const u = [...externalCrew]; u[i] = { ...u[i], phone: e.target.value }; setExternalCrew(u); }}
                          placeholder="Phone (optional)" />
                        <Field size="sm" type="text" value={ec.notes}
                          onChange={e => { const u = [...externalCrew]; u[i] = { ...u[i], notes: e.target.value }; setExternalCrew(u); }}
                          placeholder="Notes — included in their email" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Logistics Card — Transport merged in above Gear Transportation */}
          <div className={`${cardCls} space-y-5`}>
            {sectionHdr('Logistics')}

            {/* Transport & Shipping */}
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <h4 className="text-[11px] text-on-surface-variant uppercase tracking-wider font-semibold">Transport &amp; Shipping</h4>
                <LockButton lockKey="locked_logistics_transport" label="Transport" scopeValues={lockScopes.locked_logistics_transport} />
              </div>
              <div {...lockedWrapProps(form.locked_logistics_transport, 'space-y-4')}>
                {/* Transport is planned in the Transport Scheduler (full context of
                    every show's gear movement). Mode + ship dates are read-only here;
                    the button deep-links to this event in the scheduler. */}
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1.5">Transport</label>
                    {/* An assigned truck is what says "driving"; transport_mode
                        describes the external leg. Both can be true, and this used
                        to read "Shipping gear" beside an assigned truck. */}
                    <TransportChips
                      hasVehicle={assignedVehicles.length > 0}
                      transportArranged={!!form.transport_arranged}
                      transportMode={form.transport_mode}
                    />
                  </div>
                  {isEdit ? (
                    <Link
                      to={`/tour-timeline?focusEvent=${id}&focusDate=${form.ship_out_date || form.event_date || ''}`}
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors whitespace-nowrap"
                    >
                      Manage transport in Scheduler
                      <span aria-hidden>→</span>
                    </Link>
                  ) : (
                    <p className="text-[11px] text-on-surface-variant max-w-[16rem] text-right">Mode, ship dates &amp; vehicles are set in the Transport Scheduler after the event is created.</p>
                  )}
                </div>

                {assignedVehicles.length > 0 && (
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1.5">Assigned Vehicle{assignedVehicles.length > 1 ? 's' : ''}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {assignedVehicles.map(v => (
                        <span key={v.id} className="inline-flex px-2 py-1 rounded bg-sky-900/20 ring-1 ring-inset ring-sky-800/60 text-xs text-sky-200">
                          {v.name || v.display_code}
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] text-on-surface-variant mt-1">Assigned in the Transport Scheduler.</p>
                  </div>
                )}

                {tourPosition === 'middle' && (
                  <Alert tone="info" variant="note" live={false}>
                    Mid-tour event — gear travels from the previous stop. Ship dates are preserved but not active.
                  </Alert>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1">Ship-out Date</label>
                    <div className="px-3 py-2 bg-gray-900/40 border border-gray-700/30 rounded-md text-sm text-on-surface">
                      {form.ship_out_date ? formatEventDate(form.ship_out_date) : <span className="text-on-surface-disabled">— set in Scheduler</span>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1">Ship-back Date</label>
                    <div className="px-3 py-2 bg-gray-900/40 border border-gray-700/30 rounded-md text-sm text-on-surface">
                      {form.ship_back_date ? formatEventDate(form.ship_back_date) : <span className="text-on-surface-disabled">— set in Scheduler</span>}
                    </div>
                  </div>
                </div>
                {isEdit && (
                  <p className="text-[11px] text-on-surface-variant">
                    Edit ship dates in the{' '}
                    <Link
                      to={`/tour-timeline?focusEvent=${id}&focusDate=${form.ship_out_date || form.event_date || ''}`}
                      className="text-primary hover:text-primary/80"
                    >
                      Vehicle Scheduler
                    </Link>
                    . They follow the event date automatically when you change it here.
                  </p>
                )}

                {form.transport_mode === 'shipping' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1">Tracking # Outbound</label>
                      <Field type="text" value={form.tracking_number_out}
                        onChange={e => setForm(f => ({ ...f, tracking_number_out: e.target.value }))}
                        placeholder="e.g. 1Z999AA10123456784" />
                    </div>
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1">Tracking # Return</label>
                      <Field type="text" value={form.tracking_number_return}
                        onChange={e => setForm(f => ({ ...f, tracking_number_return: e.target.value }))}
                        placeholder="e.g. 1Z999AA10123456784" />
                    </div>
                  </div>
                )}

                {/* Transportation Notes — shares the Transport lock */}
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1">Transportation Notes</label>
                  <RichTextArea value={form.details_json.gear_transportation} onChange={v => setDetail('gear_transportation', v)} rows={5} />
                </div>
              </div>
            </div>

            {/* Flight */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-on-surface-variant">Flight</label>
                <LockButton lockKey="locked_logistics_flights" label="Flight" scopeValues={lockScopes.locked_logistics_flights} />
              </div>
              <div {...lockedWrapProps(form.locked_logistics_flights)}>
                <RichTextArea value={form.details_json.flight} onChange={v => setDetail('flight', v)} rows={5} />
              </div>
            </div>

            {/* Rental Car / Box Truck */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-on-surface-variant">Rental Car</label>
                  <LockButton lockKey="locked_logistics_rental_car" label="Rental Car" scopeValues={lockScopes.locked_logistics_rental_car} />
                </div>
                <div {...lockedWrapProps(form.locked_logistics_rental_car)}>
                  <RichTextArea value={form.details_json.rental_car} onChange={v => setDetail('rental_car', v)} rows={5} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-on-surface-variant">Box Truck</label>
                  <LockButton lockKey="locked_logistics_box_truck" label="Box Truck" scopeValues={lockScopes.locked_logistics_box_truck} />
                </div>
                <div {...lockedWrapProps(form.locked_logistics_box_truck)}>
                  <RichTextArea value={form.details_json.box_truck} onChange={v => setDetail('box_truck', v)} rows={5} />
                </div>
              </div>
            </div>

            {/* Hotel */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-on-surface-variant">Hotel</label>
                <LockButton lockKey="locked_logistics_hotel" label="Hotel" scopeValues={lockScopes.locked_logistics_hotel} />
              </div>
              <div {...lockedWrapProps(form.locked_logistics_hotel)}>
                <RichTextArea value={form.details_json.hotel} onChange={v => setDetail('hotel', v)} rows={5} />
              </div>
            </div>
          </div>

          {/* Packing */}
          <div className={`${cardCls} space-y-3`}>
            <div className="flex items-center justify-between">
              {sectionHdr('Packing')}
              <LockButton lockKey="locked_logistics_packing" label="Packing" scopeValues={lockScopes.locked_logistics_packing} />
            </div>
            <div {...lockedWrapProps(form.locked_logistics_packing)}>
              <RichTextArea value={form.details_json.packing} onChange={v => setDetail('packing', v)} rows={4} placeholder="Packing list, case assignments, special equipment..." />
            </div>
          </div>
          </FadeIn>)}

          {/* ═══════════════════ CONTACTS TAB ═══════════════════ */}
          {activeTab === 'contacts' && (<FadeIn key="contacts" className="space-y-5">
          <div className="flex items-center justify-center -mb-2">
            <LockButton lockKey="locked_contacts" label="Contacts" scopeValues={lockScopes.locked_contacts} />
          </div>
          <div {...lockedWrapProps(form.locked_contacts)}>
          {/* Client Contacts */}
          <div className={cardCls}>
            <div className="flex items-center justify-between mb-4">
              {sectionHdr('Contacts')}
              <button type="button" onClick={() => setForm(f => ({ ...f, contacts: [...f.contacts, { name: '', phone: '', email: '', role: '' }] }))}
                className={buttonClass('quiet', 'sm')}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                Contact
              </button>
            </div>
            {form.contacts.length === 0 && (
              <EmptyState variant="area" className="!py-3">No contacts added yet.</EmptyState>
            )}
            <div className="space-y-2.5">
              {form.contacts.map((contact, i) => (
                <div key={i} className="bg-gray-900/60 border border-gray-700/40 rounded-md p-3.5 space-y-2.5">
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1">Name</label>
                      <Field type="text" value={contact.name} onChange={e => { const u = [...form.contacts]; u[i] = { ...u[i], name: e.target.value }; set('contacts', u); }} placeholder="Name" />
                    </div>
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1">Role</label>
                      <Field type="text" value={contact.role} onChange={e => { const u = [...form.contacts]; u[i] = { ...u[i], role: e.target.value }; set('contacts', u); }} placeholder="e.g. Client, Audio, PIC" />
                    </div>
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1">Phone</label>
                      <Field type="tel" value={contact.phone} onChange={e => { const u = [...form.contacts]; u[i] = { ...u[i], phone: e.target.value }; set('contacts', u); }} placeholder="Phone" />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-on-surface-variant mb-1">Email</label>
                        <Field type="email" value={contact.email} onChange={e => { const u = [...form.contacts]; u[i] = { ...u[i], email: e.target.value }; set('contacts', u); }} placeholder="Email" />
                      </div>
                      <Tooltip label="Remove contact"><IconButton label="Remove contact" onClick={() => setForm(f => ({ ...f, contacts: f.contacts.filter((_, j) => j !== i) }))}
                        className="self-end">
                        <IconClose />
                      </IconButton></Tooltip>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Firefly Onsite Contact */}
          <div className={`${cardCls} space-y-3`}>
            {sectionHdr('Firefly Onsite Contact')}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-on-surface-variant mb-1">Name</label>
                <Field size="sm" type="text" value={form.details_json.onsite_contact_name} onChange={e => setDetail('onsite_contact_name', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-on-surface-variant mb-1">Phone</label>
                <Field size="sm" type="tel" value={form.details_json.onsite_contact_phone} onChange={e => setDetail('onsite_contact_phone', e.target.value)} />
              </div>
            </div>
          </div>
          </div>
          </FadeIn>)}

          {/* Mobile-only action buttons (header buttons hidden on mobile) */}
          <div className="flex items-center gap-3 pt-2 sm:hidden">
            <button type="submit" disabled={saving}
              className={buttonClass('filled', 'md')}>
              {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
            </button>
            <Link to={isEdit ? `/events/${id}?tab=${activeTab}` : '/events'}
              className={buttonClass('quiet', 'md')}>
              Cancel
            </Link>
          </div>
        </form>

        {/* Sidebar — always visible */}
        <div className="space-y-5">
          <Panel title="Allocation Preview" titleStyle="section" accent="primary" level={3}>
            <div className="space-y-1.5">
              <div className="flex justify-between items-baseline py-1"><span className="text-on-surface-variant text-sm">Standard (buffered)</span><span className="text-on-surface text-sm">{buffLs}</span></div>
              <div className="flex justify-between items-baseline py-1"><span className="text-on-surface-variant text-sm">Pyro (buffered)</span><span className="text-on-surface text-sm">{buffPyro}</span></div>
              <div className="flex justify-between items-baseline py-1.5 border-t border-gray-700/50 mt-1 pt-2.5"><span className="text-on-surface text-sm font-medium">Total Drones</span><span className="text-white font-bold">{totalBuffered}</span></div>
              <div className="flex justify-between items-baseline py-1"><span className="text-on-surface-variant text-sm">Batteries</span><span className="text-on-surface text-sm">{batteryCount}</span></div>
            </div>
          </Panel>

          {availability && (() => {
            const z = availability.committedByZone || {};
            const bz = availability.committedBattByZone || {};
            const hasZones = availability.committedDrones > 0 && (z.red || z.orange || z.yellow || z.green);
            return (
              <>
                <div className="rounded-md p-5" style={enoughDrones ? { backgroundColor: alpha(CATEGORY.green.mark, 0.08), border: `1px solid ${alpha(CATEGORY.green.mark, 0.2)}` } : { backgroundColor: alpha(CATEGORY.red.mark, 0.08), border: `1px solid ${alpha(CATEGORY.red.mark, 0.2)}` }}>
                  <h3 className="text-[11px] uppercase tracking-widest font-semibold mb-3" style={{ color: enoughDrones ? CATEGORY.green.ink.hex : CATEGORY.red.ink.hex }}>Fleet Availability</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between items-baseline py-0.5"><span className="text-on-surface-variant">Internal Fleet</span><span className="text-on-surface">{availability.totalFleet}</span></div>
                    <div className="flex justify-between items-baseline py-0.5"><span className="text-on-surface-variant">Committed</span><span className="text-on-surface">{availability.committedDrones}</span></div>
                    {hasZones && (
                      <div className="pl-3 space-y-0.5 py-0.5">
                        {z.red > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.red.ink.hex }}>Red zone (1-2d)</span><span className="text-xs" style={{ color: CATEGORY.red.ink.hex }}>{z.red}</span></div>}
                        {z.orange > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.orange.ink.hex }}>Orange zone (3-4d)</span><span className="text-xs" style={{ color: CATEGORY.orange.ink.hex }}>{z.orange}</span></div>}
                        {z.yellow > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.yellow.ink.hex }}>Yellow zone (5-6d)</span><span className="text-xs" style={{ color: CATEGORY.yellow.ink.hex }}>{z.yellow}</span></div>}
                        {z.green > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.green.ink.hex }}>Green zone (7+d)</span><span className="text-xs" style={{ color: CATEGORY.green.ink.hex }}>{z.green}</span></div>}
                      </div>
                    )}
                    <div className="flex justify-between items-baseline py-0.5"><span className="text-on-surface-variant">Available Drones</span><span className="font-bold" style={{ color: enoughDrones ? CATEGORY.green.ink.hex : CATEGORY.red.ink.hex }}>{availability.availableDrones}</span></div>
                    <div className="flex justify-between items-baseline py-1.5 border-t border-gray-700/50 mt-1 pt-2"><span className="text-on-surface-variant">This Event</span><span className="text-white font-medium">{totalBuffered}</span></div>
                  </div>
                  <Reveal show={!enoughDrones}>
                    <Alert tone="error" variant="note" live="status" className="mt-2.5">Not enough drones available!</Alert>
                  </Reveal>
                </div>

                <div className="rounded-md p-5" style={enoughBatteries ? { backgroundColor: alpha(CATEGORY.green.mark, 0.08), border: `1px solid ${alpha(CATEGORY.green.mark, 0.2)}` } : { backgroundColor: alpha(CATEGORY.red.mark, 0.08), border: `1px solid ${alpha(CATEGORY.red.mark, 0.2)}` }}>
                  <h3 className="text-[11px] uppercase tracking-widest font-semibold mb-3" style={{ color: enoughBatteries ? CATEGORY.green.ink.hex : CATEGORY.red.ink.hex }}>Battery Availability</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between items-baseline py-0.5"><span className="text-on-surface-variant">Total Batteries</span><span className="text-on-surface">{availability.totalBatteries}</span></div>
                    <div className="flex justify-between items-baseline py-0.5"><span className="text-on-surface-variant">Committed</span><span className="text-on-surface">{availability.committedBatteries}</span></div>
                    {hasZones && (
                      <div className="pl-3 space-y-0.5 py-0.5">
                        {bz.red > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.red.ink.hex }}>Red zone (1-2d)</span><span className="text-xs" style={{ color: CATEGORY.red.ink.hex }}>{bz.red}</span></div>}
                        {bz.orange > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.orange.ink.hex }}>Orange zone (3-4d)</span><span className="text-xs" style={{ color: CATEGORY.orange.ink.hex }}>{bz.orange}</span></div>}
                        {bz.yellow > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.yellow.ink.hex }}>Yellow zone (5-6d)</span><span className="text-xs" style={{ color: CATEGORY.yellow.ink.hex }}>{bz.yellow}</span></div>}
                        {bz.green > 0 && <div className="flex justify-between"><span className="text-xs" style={{ color: CATEGORY.green.ink.hex }}>Green zone (7+d)</span><span className="text-xs" style={{ color: CATEGORY.green.ink.hex }}>{bz.green}</span></div>}
                      </div>
                    )}
                    <div className="flex justify-between items-baseline py-0.5"><span className="text-on-surface-variant">Available Batteries</span><span className="font-bold" style={{ color: enoughBatteries ? CATEGORY.green.ink.hex : CATEGORY.red.ink.hex }}>{availability.availableBatteries}</span></div>
                    <div className="flex justify-between items-baseline py-1.5 border-t border-gray-700/50 mt-1 pt-2"><span className="text-on-surface-variant">This Event</span><span className="text-white font-medium">{batteryCount}</span></div>
                  </div>
                  <Reveal show={!enoughBatteries}>
                    <Alert tone="error" variant="note" live="status" className="mt-2.5">Not enough batteries available!</Alert>
                  </Reveal>
                </div>
              </>
            );
          })()}

          {/* Nearby events grouped by shipping zone */}
          {nearbyEvents.length > 0 && form.event_date && (() => {
            const haversine = (lat1, lon1, lat2, lon2) => {
              const R = 3958.8;
              const dLat = (lat2 - lat1) * Math.PI / 180;
              const dLon = (lon2 - lon1) * Math.PI / 180;
              const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLon/2)**2;
              return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
            };

            const target = dayjs(form.event_date);
            const visible = nearbyEvents.filter(e => e.status !== 'cancelled' && (showPendingNearby || e.status !== 'pending'));
            const pendingCount = nearbyEvents.filter(e => e.status === 'pending').length;
            const enriched = visible.map(e => {
              const days = dayjs(e.event_date).diff(target, 'day');
              const buffTotal = (e.ls_drone_count + Math.ceil(e.ls_drone_count * e.buffer_percentage / 100)) +
                (e.pyro_drone_count + Math.ceil(e.pyro_drone_count * e.buffer_percentage / 100));
              let miles = null;
              if (form.launch_latitude && e.launch_latitude) {
                miles = haversine(form.launch_latitude, form.launch_longitude, e.launch_latitude, e.launch_longitude);
              }
              const d = Math.abs(days);
              const zone = d <= 2 ? 'red' : d <= 4 ? 'orange' : d <= 6 ? 'yellow' : 'green';
              return { ...e, days, buffTotal, miles, zone };
            });

            const zones = [
              { key: 'red', label: 'Red Zone (1-2 days)', desc: 'In transit — unlikely without extreme measures' },
              { key: 'orange', label: 'Orange Zone (3-4 days)', desc: 'Likely in transit — needs direct shipping' },
              { key: 'yellow', label: 'Yellow Zone (5-6 days)', desc: 'Potentially in transit — special coordination' },
              { key: 'green', label: 'Green Zone (7+ days)', desc: 'Likely returned home' },
            ];

            const grouped = {};
            zones.forEach(z => { grouped[z.key] = enriched.filter(e => e.zone === z.key).sort((a, b) => Math.abs(a.days) - Math.abs(b.days)); });

            const renderEvent = (e) => (
              <div key={e.id} className="text-sm py-2 border-b border-gray-700/20 last:border-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <span className="text-white font-medium text-sm">{e.name}</span>
                    {e.location && <div className="text-on-surface-variant text-xs truncate mt-0.5">{e.location}</div>}
                  </div>
                  <span className="text-on-surface-variant text-xs ml-2 whitespace-nowrap font-medium">{e.buffTotal} drones</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-on-surface-variant text-xs">
                    {e.days === 0 ? 'Same day' : `${Math.abs(e.days)}d ${e.days > 0 ? 'after' : 'before'}`}
                  </span>
                  {e.miles != null && <span className="text-on-surface-variant text-xs">~{e.miles.toLocaleString()} mi</span>}
                </div>
              </div>
            );

            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[11px] uppercase tracking-widest font-semibold text-on-surface-variant">Proximity Events</h3>
                  {pendingCount > 0 && (
                    <Switch
                      checked={showPendingNearby}
                      onChange={() => setShowPendingNearby(p => !p)}
                      label={`Show Pending (${pendingCount})`}
                    />
                  )}
                </div>
                {grouped.red.length > 0 && (
                  <div className="rounded-md p-4" style={{ border: `1px solid ${alpha(CATEGORY.red.mark, 0.4)}`, backgroundColor: alpha(CATEGORY.red.mark, 0.14) }}>
                    <h3 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: CATEGORY.red.ink.hex }}>{zones[0].label}</h3>
                    <p className="text-[10px] mb-2.5" style={{ color: alpha(CATEGORY.red.ink, 0.8) }}>{zones[0].desc}</p>
                    {grouped.red.map(renderEvent)}
                  </div>
                )}
                {grouped.orange.length > 0 && (
                  <div className="rounded-md p-4" style={{ border: `1px solid ${alpha(CATEGORY.orange.mark, 0.4)}`, backgroundColor: alpha(CATEGORY.orange.mark, 0.14) }}>
                    <h3 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: CATEGORY.orange.ink.hex }}>{zones[1].label}</h3>
                    <p className="text-[10px] mb-2.5" style={{ color: alpha(CATEGORY.orange.ink, 0.8) }}>{zones[1].desc}</p>
                    {grouped.orange.map(renderEvent)}
                  </div>
                )}
                {grouped.yellow.length > 0 && (
                  <div className="rounded-md p-4" style={{ border: `1px solid ${alpha(CATEGORY.yellow.mark, 0.3)}`, backgroundColor: alpha(CATEGORY.yellow.mark, 0.14) }}>
                    <h3 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: CATEGORY.yellow.ink.hex }}>{zones[2].label}</h3>
                    <p className="text-[10px] mb-2.5" style={{ color: alpha(CATEGORY.yellow.ink, 0.8) }}>{zones[2].desc}</p>
                    {grouped.yellow.map(renderEvent)}
                  </div>
                )}
                {grouped.green.length > 0 && (
                  <div className="rounded-md p-4" style={{ border: `1px solid ${alpha(CATEGORY.green.mark, 0.3)}`, backgroundColor: alpha(CATEGORY.green.mark, 0.14) }}>
                    <h3 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: CATEGORY.green.ink.hex }}>{zones[3].label}</h3>
                    <p className="text-[10px] mb-2.5" style={{ color: alpha(CATEGORY.green.ink, 0.8) }}>{zones[3].desc}</p>
                    {grouped.green.map(renderEvent)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Same universal thread the detail page mounts. It has to be the same
              component reading the same table: while the detail page used the new
              thread and this one used event_messages, a comment posted here was
              invisible over there. MessageThread caps its own scroll height, so
              the old maxHeight prop is gone. */}
          {isEdit && <MessageThread entityType="event" entityId={id} />}
        </div>
      </div>
      {isEdit && airspaceModalOpen && (
        <EventAirspaceModal eventId={id} onClose={() => setAirspaceModalOpen(false)} />
      )}

      </EditLockGuard>
    </div>
  );
};

// Status pill for the site map URL input. Mirrors the state-enum shape in
// useSiteMapShare so the user gets immediate feedback as they paste/edit.
const SiteMapPill = ({ share, onClear }) => {
  if (share.state === 'idle') return null;
  // Suppress the red "invalid" flash mid-typing — only call something invalid
  // once it's at least URL-shaped (has `://`). A user typing `h`, `htt`, etc.
  // shouldn't see a scolding red pill on every keystroke.
  if (share.state === 'invalid' && !/^[a-z]+:\/\//i.test(String(share.shareUrl || ''))) {
    return null;
  }

  let tone = 'info';
  let text = '';
  switch (share.state) {
    case 'invalid':
      tone = 'error';
      text = 'Not a producer share URL — should look like producer.fireflystudio.app/share/…';
      break;
    case 'loading':
      tone = 'info';
      text = 'Checking link…';
      break;
    case 'broken':
      tone = 'warning';
      text = 'Producer event not found (may have been deleted). Clear or replace this URL.';
      break;
    case 'empty':
      tone = 'warning';
      text = 'Producer event has no site maps yet.';
      break;
    case 'ready':
      tone = 'success';
      text = `Linked to "${share.siteMap?.name || 'site map'}"`;
      break;
    default:
      return null;
  }
  return (
    <Alert
      tone={tone}
      variant="note"
      live="status"
      className="mt-1.5"
      action={(share.state === 'broken' || share.state === 'invalid') ? (
        <button
          type="button"
          onClick={onClear}
          className="underline text-[11px] hover:text-white"
        >
          Clear
        </button>
      ) : undefined}
    >
      {text}
    </Alert>
  );
};

// Lives inside <EditLockGuard> to consume the lock-session context (which only
// exists below the guard) and bubble it up to the parent's lockCtxRef so the
// save handler can stamp the X-Edit-Lock-Session header and signal lost-lock.
// Also pushes a JSON dump of the dirty form to the guard's snapshot so the
// LostLockModal can offer a copy-to-clipboard of unsaved work.
const LockSessionBridge = ({ ctxRef, form, formSnapshot, crew, externalCrew, isEdit }) => {
  const session = useEditLockSession();
  ctxRef.current = session;

  useEffect(() => {
    if (!isEdit || !session) { return; }
    const dirty = formSnapshot
      ? JSON.stringify({ form, crew, externalCrew, original_loaded_at: 'see formSnapshot' }, null, 2)
      : null;
    session.setDirtySnapshot(dirty);
  }, [isEdit, session, form, crew, externalCrew, formSnapshot]);

  return null;
};

export default EventForm;

